import React, { useEffect, useRef, useState } from 'react';
import { Save, CheckCircle2, Upload, Image as ImageIcon, X, Sparkles, Loader2 } from 'lucide-react';
import { contentApi, uploadsApi, i18nApi } from '../../lib/api';
import { LANGS } from '../../lib/langs';
import UI from '../../lib/uiCatalog';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { invalidateContentCache } from '../../hooks/useContent';

// Content field groups
const groups = [
  {
    title: 'Header & Branding',
    description: 'Logo text and tagline in the top navigation.',
    fields: [
      { key: 'brand_name',    label: 'Brand name (logo)',     type: 'text' },
      { key: 'brand_tagline', label: 'Brand tagline (small)', type: 'text' },
    ],
  },
  {
    title: 'Homepage — Hero',
    description: 'The main banner area on the homepage.',
    fields: [
      { key: 'hero_eyebrow',   label: 'Hero eyebrow (small badge)', type: 'text' },
      { key: 'hero_title',     label: 'Hero title',                 type: 'text' },
      { key: 'hero_subtitle',  label: 'Hero subtitle',              type: 'textarea' },
      { key: 'hero_image',     label: 'Hero image',                 type: 'image' },
      { key: 'hero_cta_label', label: 'Hero button label',          type: 'text' },
      { key: 'hero_feature_1', label: 'Feature #1',                 type: 'text' },
      { key: 'hero_feature_2', label: 'Feature #2',                 type: 'text' },
      { key: 'hero_feature_3', label: 'Feature #3',                 type: 'text' },
      { key: 'hero_feature_4', label: 'Feature #4',                 type: 'text' },
      { key: 'hero_feature_5', label: 'Feature #5',                 type: 'text' },
    ],
  },
  {
    title: 'Homepage — Section headings',
    description: 'Titles for the sections below the hero.',
    fields: [
      { key: 'products_eyebrow', label: 'Products eyebrow',    type: 'text' },
      { key: 'products_title',   label: 'Products title',      type: 'text' },
      { key: 'why_eyebrow',      label: 'Why-choose eyebrow',  type: 'text' },
      { key: 'why_title',        label: 'Why-choose title',    type: 'text' },
      { key: 'how_eyebrow',      label: 'How-it-works eyebrow',type: 'text' },
      { key: 'how_title',        label: 'How-it-works title',  type: 'text' },
      { key: 'reviews_eyebrow',  label: 'Reviews eyebrow',     type: 'text' },
      { key: 'reviews_title',    label: 'Reviews title',       type: 'text' },
      { key: 'faq_eyebrow',      label: 'FAQ eyebrow',         type: 'text' },
      { key: 'faq_title',        label: 'FAQ title',           type: 'text' },
    ],
  },
  {
    title: 'Homepage — Bottom CTA',
    description: 'Final call-to-action band at the bottom of the homepage.',
    fields: [
      { key: 'cta_title',    label: 'CTA title',    type: 'text' },
      { key: 'cta_subtitle', label: 'CTA subtitle', type: 'text' },
      { key: 'cta_button',   label: 'CTA button label', type: 'text' },
    ],
  },
  {
    title: 'About page',
    description: 'Copy shown on the About page.',
    fields: [
      { key: 'about_intro', label: 'About intro', type: 'textarea' },
    ],
  },
  {
    title: 'Footer & Contact',
    description: 'Company info shown in the footer and contact page.',
    fields: [
      { key: 'footer_tagline',       label: 'Footer tagline',    type: 'textarea' },
      { key: 'contact_email',        label: 'Contact email',     type: 'text' },
      { key: 'contact_phone',        label: 'Contact phone',     type: 'text' },
      { key: 'contact_address',      label: 'Contact address',   type: 'text' },
      { key: 'footer_company_name',  label: 'Company legal name',type: 'text' },
      { key: 'footer_office_address',label: 'Office address',    type: 'text' },
      { key: 'footer_vat',           label: 'EU VAT no',         type: 'text' },
      { key: 'social_facebook',      label: 'Facebook URL',      type: 'text' },
      { key: 'social_instagram',     label: 'Instagram URL',     type: 'text' },
      { key: 'social_twitter',       label: 'Twitter / X URL',   type: 'text' },
      { key: 'footer_copyright',     label: 'Footer copyright text', type: 'text' },
    ],
  },
];

// Fields that are edited on all languages (URLs/images/emails/vat only editable on English)
const NON_TRANSLATABLE_KEYS = new Set([
  'hero_image',
  'contact_email', 'contact_phone', 'footer_vat',
  'social_facebook', 'social_instagram', 'social_twitter',
]);

export default function AdminContent() {
  const [lang, setLang] = useState('en');
  const [content, setContent] = useState({});
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState('');
  const [lastUpload, setLastUpload] = useState(null);
  const [translating, setTranslating] = useState(false);
  const [translateMsg, setTranslateMsg] = useState('');
  const fileInputs = useRef({});

  // Load content for currently selected language
  useEffect(() => {
    let alive = true;
    contentApi.getAdmin(lang).then((data) => { if (alive) setContent(data || {}); }).catch(() => {});
    return () => { alive = false; };
  }, [lang]);

  const save = async () => {
    setSaving(true); setSaved(false);
    try {
      await contentApi.update(content, lang);
      invalidateContentCache();
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } finally { setSaving(false); }
  };

  const autoTranslate = async () => {
    if (!window.confirm('Auto-translate every English text field AND every UI label into all 15 other languages? Existing translations are preserved. This may take a few minutes.')) return;
    setTranslating(true);
    try {
      const targetLangs = LANGS.filter((l) => l.code !== 'en').map((l) => l.code);
      const contentSummary = [];
      let uiTotal = 0;

      // Run BOTH site content + UI catalog per-language so no single request
      // exceeds the ~60s Kubernetes ingress timeout (the previous single call
      // that fanned out to all 15 languages was returning 502).
      for (let i = 0; i < targetLangs.length; i++) {
        const code = targetLangs[i];
        setTranslateMsg(`Translating ${code.toUpperCase()} (${i + 1}/${targetLangs.length})…`);
        try {
          const cr = await contentApi.translate([code], true);
          const n = cr?.results?.[code]?.translated || 0;
          if (n > 0) contentSummary.push(`${code}: ${n}`);
        } catch (err) {
          console.warn(`Content translate ${code} failed`, err);
        }
        try {
          const r = await i18nApi.translateOneLang(code, UI, true);
          uiTotal += r.translated_count || 0;
        } catch (err) {
          console.warn(`UI translate ${code} failed`, err);
        }
      }

      invalidateContentCache();
      const parts = [];
      if (contentSummary.length) parts.push(`Site content — ${contentSummary.join(', ')}`);
      if (uiTotal) parts.push(`UI strings — ${uiTotal} added`);
      setTranslateMsg(parts.length ? `Done. ${parts.join(' · ')}` : 'Done — all languages already up to date.');

      // Refresh the currently viewed language
      if (lang !== 'en') {
        const data = await contentApi.getAdmin(lang);
        setContent(data || {});
      }
      setTimeout(() => setTranslateMsg(''), 8000);
    } catch (err) {
      setTranslateMsg(err?.response?.data?.detail || 'Auto-translate failed');
    } finally {
      setTranslating(false);
    }
  };

  const onImage = async (key, file) => {
    if (!file) return;
    setUploading(key);
    try {
      const res = await uploadsApi.uploadImage(file);
      const fullUrl = uploadsApi.publicUrl(res.url);
      setContent((c) => ({ ...c, [key]: fullUrl }));
      if (res.compressed && res.original_size && res.size) {
        const savedPct = Math.max(0, Math.round(100 - (res.size / res.original_size) * 100));
        const fmt = (b) => (b >= 1024 * 1024 ? (b / (1024 * 1024)).toFixed(1) + ' MB' : Math.max(1, Math.round(b / 1024)) + ' KB');
        // Small non-blocking toast via alert() replacement
        console.log(`Image auto-compressed: ${fmt(res.original_size)} -> ${fmt(res.size)} WebP (-${savedPct}%)`);
        setLastUpload({ key, orig: fmt(res.original_size), out: fmt(res.size), savedPct });
        setTimeout(() => setLastUpload(null), 6000);
      }
    } catch (err) {
      alert('Upload failed: ' + (err?.response?.data?.detail || err.message));
    } finally {
      setUploading('');
      if (fileInputs.current[key]) fileInputs.current[key].value = '';
    }
  };

  const isReadOnly = (key) => lang !== 'en' && NON_TRANSLATABLE_KEYS.has(key);

  return (
    <div>
      <div className="mb-5 flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold text-[#0b1f3a]">Site content</h2>
          <p className="text-slate-500 text-sm mt-1">
            Edit all the text and images shown across the public site. Switch language below to translate. Changes take effect immediately.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {saved && (
            <span className="inline-flex items-center gap-1.5 text-sm text-emerald-700">
              <CheckCircle2 size={16} /> Saved
            </span>
          )}
          <button onClick={save} disabled={saving} className="btn-primary disabled:opacity-60" data-testid="admin-content-save">
            <Save size={16} /> {saving ? 'Saving...' : 'Save changes'}
          </button>
        </div>
      </div>

      {lastUpload && (
        <div
          className="mb-4 rounded-xl border border-emerald-200 bg-emerald-50 text-emerald-800 px-4 py-2.5 text-sm flex items-center gap-2"
          data-testid="admin-content-compress-toast"
        >
          <CheckCircle2 size={16} className="shrink-0" />
          <span>
            Image auto-compressed: <b>{lastUpload.orig}</b> &rarr; <b>{lastUpload.out}</b> WebP
            <span className="ml-2 text-emerald-700">(-{lastUpload.savedPct}%)</span>
          </span>
        </div>
      )}

      {/* Translation control panel */}
      <div className="mb-5 bg-white border border-slate-200 rounded-2xl p-4">
        <div className="flex flex-wrap items-center gap-3 justify-between">
          <div className="flex flex-col gap-2">
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Editing language</div>
            <div className="flex flex-wrap gap-1.5" data-testid="admin-content-lang-tabs">
              {LANGS.map((l) => (
                <button
                  key={l.code}
                  type="button"
                  onClick={() => setLang(l.code)}
                  data-testid={`admin-content-lang-${l.code}`}
                  className={`h-9 min-w-[64px] px-3 rounded-lg text-xs font-semibold border transition-colors inline-flex items-center gap-1.5 ${
                    lang === l.code
                      ? 'bg-[#0d3a8a] text-white border-[#0d3a8a]'
                      : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                  }`}
                >
                  <span>{l.flag}</span>
                  <span>{l.code.toUpperCase()}</span>
                </button>
              ))}
            </div>
          </div>
          <div className="flex flex-col items-end gap-2">
            <button
              type="button"
              onClick={autoTranslate}
              disabled={translating}
              data-testid="admin-content-translate-all"
              className="btn-primary disabled:opacity-60"
            >
              {translating ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
              {translating ? 'Translating…' : 'Translate EN → 15 languages'}
            </button>
            {translateMsg && (
              <p className="text-xs text-slate-500 max-w-xs text-right">{translateMsg}</p>
            )}
          </div>
        </div>
        {lang !== 'en' && (
          <p className="mt-3 text-xs text-slate-500">
            You are editing <b className="text-[#0d3a8a]">{LANGS.find((l) => l.code === lang)?.name}</b>. Leave a field empty to fall back to the English original on the site. Images, phone, email, VAT and social links are only editable in English.
          </p>
        )}
      </div>

      <div className="space-y-6">
        {groups.map((g) => (
          <div key={g.title} className="bg-white border border-slate-200 rounded-2xl p-6">
            <div className="mb-5">
              <h3 className="font-semibold text-[#0b1f3a]">{g.title}</h3>
              <p className="text-sm text-slate-500 mt-0.5">{g.description}</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {g.fields.map((f) => {
                const value = content[f.key] ?? '';
                const readOnly = isReadOnly(f.key);
                if (f.type === 'image') {
                  return (
                    <div key={f.key} className="md:col-span-2">
                      <Label className="field-label">{f.label}{readOnly && <span className="text-xs text-slate-400 font-normal ml-2">(only editable in English)</span>}</Label>
                      <div className="flex items-start gap-4">
                        {value ? (
                          <div className="relative w-56 h-36 rounded-xl overflow-hidden border border-slate-200 bg-slate-50 shrink-0">
                            <img src={value} alt="" className="w-full h-full object-cover" />
                            {!readOnly && (
                              <button
                                type="button"
                                onClick={() => setContent({ ...content, [f.key]: '' })}
                                className="absolute top-1 right-1 w-6 h-6 rounded-full bg-white/90 text-slate-700 hover:bg-white inline-flex items-center justify-center shadow"
                                title="Clear image"
                              >
                                <X size={12} />
                              </button>
                            )}
                          </div>
                        ) : (
                          <div className="w-56 h-36 rounded-xl border-2 border-dashed border-slate-200 bg-slate-50 flex items-center justify-center text-slate-400 shrink-0">
                            <ImageIcon size={26} />
                          </div>
                        )}
                        <div className="flex-1 space-y-2">
                          <Input
                            value={value}
                            disabled={readOnly}
                            onChange={(e) => setContent({ ...content, [f.key]: e.target.value })}
                            placeholder="Paste an image URL or upload below"
                          />
                          {!readOnly && (
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                onClick={() => fileInputs.current[f.key]?.click()}
                                disabled={uploading === f.key}
                                className="btn-outline !py-2 !px-3 text-xs disabled:opacity-60"
                              >
                                <Upload size={14} /> {uploading === f.key ? 'Uploading...' : 'Upload image'}
                              </button>
                              <p className="text-xs text-slate-500">JPG / PNG / WebP up to 8 MB</p>
                            </div>
                          )}
                          <input
                            ref={(el) => { fileInputs.current[f.key] = el; }}
                            type="file"
                            accept="image/*"
                            hidden
                            onChange={(e) => onImage(f.key, e.target.files?.[0])}
                          />
                        </div>
                      </div>
                    </div>
                  );
                }
                if (f.type === 'textarea') {
                  return (
                    <div key={f.key} className="md:col-span-2">
                      <Label className="field-label">{f.label}{readOnly && <span className="text-xs text-slate-400 font-normal ml-2">(only editable in English)</span>}</Label>
                      <Textarea rows={3} value={value} disabled={readOnly} onChange={(e) => setContent({ ...content, [f.key]: e.target.value })} />
                    </div>
                  );
                }
                return (
                  <div key={f.key}>
                    <Label className="field-label">{f.label}{readOnly && <span className="text-xs text-slate-400 font-normal ml-2">(only editable in English)</span>}</Label>
                    <Input value={value} disabled={readOnly} onChange={(e) => setContent({ ...content, [f.key]: e.target.value })} />
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-6 flex items-center gap-3">
        <button onClick={save} disabled={saving} className="btn-primary disabled:opacity-60">
          <Save size={16} /> {saving ? 'Saving...' : 'Save all changes'}
        </button>
        {saved && (
          <span className="inline-flex items-center gap-1.5 text-sm text-emerald-700">
            <CheckCircle2 size={16} /> Saved
          </span>
        )}
      </div>
    </div>
  );
}
